// Verification of matched candidates against line item pricing.

import { round2, isSameMonth, isSameOrAdjacentMonth, sumAmounts, vatAmount } from '../../../calc.ts';
import type { Pricing } from '@shared/types.ts';
import type { FieldMismatch, VerifyResult } from './types.ts';
import { MATCH_MONEY_TOLERANCE } from './scoreConfig.ts';

// REC-m7: single money tolerance shared with the reconcile report (was 0.015 here,
// 0.01 in the report — now both 0.01). Subset-sum target stays exact (see scoreConfig).
const TOLERANCE = MATCH_MONEY_TOLERANCE;

/** Normalize a vat_rate_code for STRICT comparison: lowercased, with null/undefined kept
 *  as an empty sentinel — NOT defaulted to a rate. A missing code never equals '23'; two
 *  missing codes compare equal. (The matcher does not assume a default VAT rate.) */
export function normVat(code: string | null | undefined): string {
  return code == null ? '' : code.toLowerCase();
}

function checkNumeric(
  mismatches: FieldMismatch[],
  field: string,
  expected: number,
  actual: number,
  tolerance: number,
): void {
  const diff = Math.abs(actual - expected);
  if (tolerance === 0 ? actual !== expected : diff > tolerance) {
    mismatches.push({ field, expected, actual });
  }
}

/** Verify the quantity basis for a matched set against the line.
 *  - Weight-priced (per_ton) line: the summed candidate quantity (= summed weight) must
 *    equal the line quantity exactly.
 *  - Flat-rate line: a flat fee may be billed per TRIP or per TONNE, so accept the match
 *    when EITHER the transaction COUNT or the summed WEIGHT equals the line quantity. */
function checkQuantity(
  mismatches: FieldMismatch[],
  lineItem: Pricing,
  summedQuantity: number,
  count: number,
  summedWeight: number | null,
): void {
  const expected = round2(lineItem.quantity ?? 0);
  if (lineItem.price_type === 'flat_rate') {
    const byCount = count === expected;
    const byWeight = summedWeight != null && round2(summedWeight) === expected;
    if (!byCount && !byWeight) {
      mismatches.push({ field: 'quantity', expected, actual: count });
    }
    return;
  }
  if (round2(summedQuantity) !== expected) {
    mismatches.push({ field: 'quantity', expected, actual: round2(summedQuantity) });
  }
}

/** Verify a single candidate's pricing against a line item. */
/** Date gate: same calendar month, OR (for already-paid invoices) an adjacent month. */
function dateOutsideWindow(candidateDate: string, sellDate: string, allowAdjacentMonth: boolean): boolean {
  return allowAdjacentMonth
    ? !isSameOrAdjacentMonth(candidateDate, sellDate)
    : !isSameMonth(candidateDate, sellDate);
}

export function verifySingleMatch(
  lineItem: Pricing,
  candidate: Pricing,
  sellDate?: string | null,
  candidateDate?: string | null,
  candidateWeight?: number | null,
  allowAdjacentMonth = false,
): VerifyResult {
  const mismatches: FieldMismatch[] = [];

  // When price types differ or are unknown (e.g. invoice flat_rate vs candidate per_ton),
  // quantity and unit_price are not comparable — only check net/vat/gross.
  const crossPriceType = lineItem.price_type !== candidate.price_type;

  if (crossPriceType) {
    checkNumeric(mismatches, 'net_amount', round2(lineItem.net_amount ?? 0), round2(candidate.net_amount ?? 0), TOLERANCE);
    checkNumeric(mismatches, 'vat_amount', round2(lineItem.vat_amount ?? 0), round2(candidate.vat_amount ?? 0), TOLERANCE);
    checkNumeric(mismatches, 'gross_amount', round2(lineItem.gross_amount ?? 0), round2(candidate.gross_amount ?? 0), TOLERANCE);
  } else {
    checkNumeric(mismatches, 'net_amount', round2(lineItem.net_amount ?? 0), round2(candidate.net_amount ?? 0), TOLERANCE);
    checkNumeric(mismatches, 'vat_amount', round2(lineItem.vat_amount ?? 0), round2(candidate.vat_amount ?? 0), TOLERANCE);
    checkNumeric(mismatches, 'gross_amount', round2(lineItem.gross_amount ?? 0), round2(candidate.gross_amount ?? 0), TOLERANCE);
    // One candidate = a count of 1; flat_rate also accepts a weight-basis match.
    checkQuantity(mismatches, lineItem, round2(candidate.quantity ?? 0), 1, candidateWeight ?? null);
    checkNumeric(mismatches, 'unit_price', round2(lineItem.unit_price ?? 0), round2(candidate.unit_price ?? 0), 0);
  }

  // VAT rate code must match (strict, case-insensitive). The code is authoritative:
  // a line coded '0' is a 0%/exempt line and must NOT be linked to a 23% transaction,
  // even if the amounts coincidentally agree. A genuine code-vs-amount inconsistency on
  // the invoice (e.g. coded '0' but charging 23%) is bad source data to fix in
  // Fakturownia — the matcher leaves it unmatched rather than guessing.
  if (normVat(candidate.vat_rate_code) !== normVat(lineItem.vat_rate_code)) {
    mismatches.push({ field: 'vat_rate_code', expected: lineItem.vat_rate_code, actual: candidate.vat_rate_code });
  }

  // Date gate: the transaction's side-date (load_date for load side, unload_date
  // for unload/transport — already resolved into `candidateDate` by the candidate
  // view) must be in the SAME CALENDAR MONTH as the invoice sell_date — UNLESS the
  // invoice is already paid, in which case an adjacent month is accepted too (paid
  // invoices are settled history; a quarterly straggler a month off is tolerated).
  // For unpaid invoices the gate stays strict so cross-month discrepancies surface
  // before payment.
  if (sellDate && candidateDate && dateOutsideWindow(candidateDate, sellDate, allowAdjacentMonth)) {
    mismatches.push({ field: 'date', expected: sellDate.slice(0, 7), actual: candidateDate });
  }

  return { verified: mismatches.length === 0, mismatches };
}

/** Verify multiple candidates' aggregated pricing against a line item. */
export function verifyMultiMatch(
  lineItem: Pricing,
  candidates: Pricing[],
  sellDate?: string | null,
  candidateDates?: (string | null)[],
  candidateWeights?: (number | null)[],
  allowAdjacentMonth = false,
): VerifyResult {
  if (candidates.length === 0) {
    return { verified: false, mismatches: [{ field: 'candidates', expected: '>0', actual: '0' }] };
  }

  if (candidates.length === 1) {
    return verifySingleMatch(lineItem, candidates[0], sellDate, candidateDates?.[0], candidateWeights?.[0], allowAdjacentMonth);
  }

  const mismatches: FieldMismatch[] = [];

  // When the invoice line and the candidates are priced on different bases
  // (e.g. invoice per-trip vs transactions per-ton), the line's unit_price/quantity
  // aren't comparable to the candidates' — reconcile on the net/vat/gross totals.
  // Requires a KNOWN line price_type (non-null) that genuinely differs. Even then we
  // still require the candidates to be INTERNALLY consistent (same unit rate) so a
  // coincidental subset-sum of mismatched-rate transactions can't slip through.
  const crossPriceType = lineItem.price_type != null && candidates.some(c => c.price_type !== lineItem.price_type);

  const sums = sumAmounts(candidates);
  // Compute VAT/gross from aggregate net × the line's own rate code (same as the invoice
  // does) instead of summing individually-rounded per-candidate VATs. The code is used
  // as-is (no '23' default): a missing code yields a 0 rate, so an inconsistent line is
  // caught by the vat_amount check rather than silently assumed to be 23%.
  const computedVat = vatAmount(sums.net, normVat(lineItem.vat_rate_code));
  const computedGross = round2(sums.net + computedVat);

  if (!crossPriceType) {
    const summedWeight = candidateWeights
      ? round2(candidateWeights.reduce<number>((acc, w) => acc + (w ?? 0), 0))
      : null;
    checkQuantity(mismatches, lineItem, sums.quantity, candidates.length, summedWeight);
  }
  checkNumeric(mismatches, 'net_amount', round2(lineItem.net_amount ?? 0), sums.net, TOLERANCE);
  checkNumeric(mismatches, 'vat_amount', round2(lineItem.vat_amount ?? 0), computedVat, TOLERANCE);
  checkNumeric(mismatches, 'gross_amount', round2(lineItem.gross_amount ?? 0), computedGross, TOLERANCE);

  const expectedUnitPrice = round2(lineItem.unit_price ?? 0);
  const firstUnitPrice = round2(candidates[0].unit_price ?? 0);
  const expectedVatRateCode = normVat(lineItem.vat_rate_code);

  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    const actualUnitPrice = round2(c.unit_price ?? 0);
    if (crossPriceType) {
      // Different basis from the line → compare candidates to EACH OTHER, not the line.
      if (actualUnitPrice !== firstUnitPrice) {
        mismatches.push({ field: `unit_price[${i}]`, expected: firstUnitPrice, actual: actualUnitPrice });
      }
    } else if (actualUnitPrice !== expectedUnitPrice) {
      mismatches.push({ field: `unit_price[${i}]`, expected: expectedUnitPrice, actual: actualUnitPrice });
    }
    // VAT rate code must match per candidate (strict, case-insensitive) — see
    // verifySingleMatch. The code is authoritative; a coincidental amount agreement
    // does not license linking a differently-rated transaction.
    if (normVat(c.vat_rate_code) !== expectedVatRateCode) {
      mismatches.push({ field: `vat_rate_code[${i}]`, expected: lineItem.vat_rate_code, actual: c.vat_rate_code });
    }
    const d = candidateDates?.[i];
    // Same-month gate (adjacent month allowed for paid invoices) — see verifySingleMatch.
    if (sellDate && d && dateOutsideWindow(d, sellDate, allowAdjacentMonth)) {
      mismatches.push({ field: `date[${i}]`, expected: sellDate.slice(0, 7), actual: d });
    }
  }

  return { verified: mismatches.length === 0, mismatches };
}
